import React, { useEffect, useState } from 'react';
import { DynamicIcon } from 'lucide-react/dynamic';
import {
    Bot, Box, CircleDot, KeyRound, LayoutGrid, MessageCircleQuestion, Puzzle, User, Users,
} from 'lucide-react';
import api from '../../services/api';

// FACES — how a thing shows itself in Nucleus, everywhere, one way (design.oS,
// "Faces"). A thing with a picture shows its picture. Anything else shows an
// ICON on a quiet wash of its hue:
//
//   a node / a schema  a square, the schema's own icon (schema_metadata.icon,
//                      a Lucide name the schema's author chose)
//   an app             its logo, else a square with the app glyph
//   a plugin           its logo, else a square with the plugin glyph (or its trigger's)
//   a person, an identity  their photo, else a square with the person glyph
//   a group            a square with the group glyph
//
// Every face is square (design.oS, "Square").

// ── schema icons, read once per session ────────────────────────────────────
let schemaIcons = null;   // Map: schema id and schema name → Lucide icon name
let pending = null;
const listeners = new Set();
function loadSchemaIcons() {
    if (schemaIcons || pending) return;
    pending = (api.getSchemas?.({ limit: 1000 }) || Promise.resolve([]))
        .then((list) => {
            const m = new Map();
            (Array.isArray(list) ? list : list?.schemas || []).forEach((s) => {
                const icon = s?.schema_metadata?.icon;
                if (!icon) return;
                if (s.id) m.set(String(s.id), icon);
                if (s.schema_name) {
                    m.set(s.schema_name, icon);
                    // a namespaced name ("golden:belief") answers to its short name too
                    const short = String(s.schema_name).split(':').pop();
                    if (!m.has(short)) m.set(short, icon);
                }
            });
            schemaIcons = m;
        })
        .catch(() => { schemaIcons = new Map(); })
        .finally(() => { pending = null; listeners.forEach((f) => f()); });
}

/** The Lucide name a schema chose for itself, or null (by id or by name). */
export function useSchemaIcon(idOrName) {
    const [, tick] = useState(0);
    useEffect(() => {
        if (schemaIcons) return undefined;
        const f = () => tick((n) => n + 1);
        listeners.add(f);
        loadSchemaIcons();
        return () => listeners.delete(f);
    }, []);
    if (!idOrName || !schemaIcons) return null;
    const k = String(idOrName);
    return schemaIcons.get(k) || schemaIcons.get(k.split(':').pop()) || null;
}

/** A schema's icon: its own, else a dot for freeform, else a box. */
export function SchemaIcon({ schema, id, size = 14, strokeWidth = 1.7, className }) {
    const name = useSchemaIcon(id || schema);
    if (name) return <DynamicIcon name={name} size={size} strokeWidth={strokeWidth} className={className} fallback={() => <Box size={size} strokeWidth={strokeWidth} />} aria-hidden="true" />;
    const Fallback = !schema || /^freeform$/i.test(schema) ? CircleDot : Box;
    return <Fallback size={size} strokeWidth={strokeWidth} className={className} aria-hidden="true" />;
}

/** The house shape: a square on a wash of a hue, an icon inside. */
export function Glyphed({ hue, size = 50, children, className = '', title }) {
    return (
        <span
            className={`nx-face ${className}`}
            style={{ '--face-hue': hue || 'var(--nx-zone-hue, var(--ofself-ink))', width: size, height: size }}
            title={title}
            aria-hidden="true"
        >
            {children}
        </span>
    );
}

const inner = (size) => Math.max(10, Math.round(size * 0.44));

/** A node's (or a schema's) square: its kind's hue, the schema's icon. */
export function NodeMark({ schema, schemaId, hue, size = 22 }) {
    return (
        <Glyphed hue={hue} size={size}>
            <SchemaIcon schema={schema} id={schemaId} size={inner(size)} strokeWidth={size < 30 ? 1.8 : 1.5} />
        </Glyphed>
    );
}

/** A photo where there is one (signed URLs expire: a failed one falls back), else the glyph. */
function PhotoOr({ url, size, children }) {
    const [failed, setFailed] = useState(null);
    if (url && url !== failed) {
        return <img className="nx-face-photo" src={url} alt="" width={size} height={size} onError={() => setFailed(url)} />;
    }
    return children;
}

export function PersonFace({ url, size = 50, hue }) {
    return (
        <PhotoOr url={url} size={size}>
            <Glyphed hue={hue || 'var(--nx-zone-external)'} size={size}><User size={inner(size)} strokeWidth={1.6} /></Glyphed>
        </PhotoOr>
    );
}

export function GroupFaceIcon({ url, size = 50 }) {
    return (
        <PhotoOr url={url} size={size}>
            <Glyphed hue="var(--nx-zone-external)" size={size}><Users size={inner(size)} strokeWidth={1.6} /></Glyphed>
        </PhotoOr>
    );
}

export function AgentFace({ size = 50, hue }) {
    return <Glyphed hue={hue} size={size}><Bot size={inner(size)} strokeWidth={1.6} /></Glyphed>;
}

/** An access token: a key, on the gate's hue. Someone made it for a script or tool
 *  of their own, so it has no logo and no face but this. */
export function TokenFace({ size = 50, hue }) {
    return <Glyphed hue={hue || 'var(--nx-zone-gates)'} size={size}><KeyRound size={inner(size)} strokeWidth={1.6} /></Glyphed>;
}

/** A plugin: its logo when it has one (`logo`, the plugin's logo_url), else the
 *  plugin glyph (or `children`, an automation's trigger or a flow's glyph) on
 *  the section's hue. One face for every plugin: never a hue of its own. */
export function PluginFace({ size = 50, hue, logo, children }) {
    return (
        <PhotoOr url={logo} size={size}>
            <Glyphed hue={hue || 'var(--nx-zone-external)'} size={size}>{children || <Puzzle size={inner(size)} strokeWidth={1.6} />}</Glyphed>
        </PhotoOr>
    );
}

/** A question in the review queue. */
export function QuestionFace({ size = 50 }) {
    return <Glyphed size={size}><MessageCircleQuestion size={inner(size)} strokeWidth={1.6} /></Glyphed>;
}

/** An app without a logo of its own. */
export function AppGlyph({ size = 50, hue }) {
    return <Glyphed hue={hue || 'var(--nx-zone-external)'} size={size}><LayoutGrid size={inner(size)} strokeWidth={1.6} /></Glyphed>;
}

